import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

const ALL_PAGES = [
  {
    path: '/governing-documents',
    title: 'Governing Documents',
    description: 'Brookline plans, by-laws, policies and other governing documents',
    icon: '📜',
  },
  {
    path: '/dashboards',
    title: 'Brookline Notepad Dashboards',
    description: 'Town plans and their goals, strategies and actions - the 2030 Roadmap, CARP, Vision Zero and the rest',
    icon: '📊',
  },
  {
    path: '/memos',
    title: 'Memos',
    description: 'Memos for Select Board review',
    icon: '📝',
  },
  {
    path: '/notepad',
    title: 'Brookline Notepad Blog',
    description: 'Reflections on life and governance in Brookline',
    icon: '✏️',
  },
  {
    path: '/faqs',
    title: 'Brookline Notepad FAQs',
    description: 'Frequently asked questions on Brookline warrant articles and studies, hosted for their petitioners',
    icon: '❓',
  },
  {
    path: '/studies',
    title: 'Brookline Notepad Studies',
    description: 'Studies and comparisons - development massing, school staffing and town budgets',
    icon: '🔬',
  },
];

function Home() {
  const { loading, hasAccess } = useAuth();
  const mainRef = useRef(null);

  useEffect(() => {
    document.title = 'Brookline Notepad';
  }, []);

  // The header is position:fixed on wide screens (so it overlays content and the
  // content must clear it) but in normal flow on narrow screens (where the base
  // CSS padding is correct). Set the content's top padding to the header's actual
  // height only while the header is fixed, and keep it in sync on resize. The CSS
  // also carries a fixed fallback (see Home.css) so the header is never obscured
  // before this runs. This keeps the spacing tight and dynamic without the
  // stale-value problems of a CSS variable across resizes.
  useEffect(() => {
    const header = document.querySelector('.site-header');
    const main = mainRef.current;
    if (!header || !main) return;
    const update = () => {
      if (getComputedStyle(header).position === 'fixed') {
        main.style.paddingTop = `${header.offsetHeight + 16}px`;
      } else {
        main.style.paddingTop = '';
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Show only pages the visitor can actually open under the access model.
  const visiblePages = ALL_PAGES.filter(page => hasAccess(page.path));

  // Home carries its own sitewide disclaimer wording (no standard prefix); the
  // two sentences sit in one paragraph, separated by a space.
  const disclaimerFooter = (
    <DisclaimerFooter standardPrefix={false}>
      <p>
        Neither this web page nor any other page on this website are official
        documents of the Town of Brookline nor of its Select Board. Other web
        pages on this website may contain additional disclaimers or source
        information.
      </p>
    </DisclaimerFooter>
  );

  if (loading) {
    return (
      <div className="home-page">
        <Header title="Brookline Notepad" />
        <main className="home-content">
          <div style={{ textAlign: 'center', color: '#4b5563', padding: '3rem' }}>Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="home-page">
      <a href="#main-content" className="home-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" />

      <main id="main-content" tabIndex={-1} className="home-content" ref={mainRef}>
        <div className="page-list">
          {visiblePages.map((page) => (
            <div key={page.path} className="page-card">
              <div className="page-icon">{page.icon}</div>
              <div className="page-info">
                <h2 className="page-card-title">{page.title}</h2>
                <p>{page.description}</p>
                <div className="page-links">
                  <Link to={page.path} className="view-link">View Page</Link>
                  <button
                    className="copy-link"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + page.path);
                      alert('Link copied to clipboard!');
                    }}
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {disclaimerFooter}
      </main>
    </div>
  );
}

export default Home;
